import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { trackProductEvent } from "@/lib/productMetrics";
import { Globe2, Loader2, ShieldCheck, Sparkles, X } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatConfidence = (value) => `${Math.round((value || 0) * 100)}%`;

const GlobalSongMatchPanel = ({ analysis }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileLoadingId, setProfileLoadingId] = useState(null);
  const [rebuildingId, setRebuildingId] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const queryParams = useMemo(
    () => ({
      title: analysis?.filename || "",
      duration: analysis?.duration,
      bpm: analysis?.bpm,
      key: analysis?.key,
    }),
    [analysis?.bpm, analysis?.duration, analysis?.filename, analysis?.key]
  );

  const fetchCandidates = useCallback(async () => {
    if (!analysis?.id || !queryParams.title) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/global-songs/match-candidates`, {
        headers: getAuthHeaders(),
        params: queryParams,
      });
      setCandidates(response.data || []);
    } catch (error) {
      // Global matching is additive. Do not block the analysis/practice experience.
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [analysis?.id, getAuthHeaders, queryParams]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const linkAnalysis = async (globalSongId) => {
    setLinkingId(globalSongId);
    try {
      await axios.post(
        `${API_URL}/global-songs/link-analysis/${analysis.id}`,
        { global_song_id: globalSongId, confirm_match: true },
        { headers: getAuthHeaders() }
      );
      toast.success("Análisis asociado al mapa musical global");
      await rebuildProfile(globalSongId);
      await fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No fue posible asociar la canción global");
    } finally {
      setLinkingId(null);
    }
  };

  const fetchProfile = async (globalSongId) => {
    setProfileLoadingId(globalSongId);
    try {
      const response = await axios.get(`${API_URL}/global-songs/${globalSongId}/analysis-profile`, {
        headers: getAuthHeaders(),
      });
      setProfile(response.data);
    } catch (error) {
      toast.info("Esta canción global todavía no tiene perfil agregado.");
    } finally {
      setProfileLoadingId(null);
    }
  };

  const rebuildProfile = async (globalSongId) => {
    setRebuildingId(globalSongId);
    try {
      const response = await axios.post(
        `${API_URL}/global-songs/${globalSongId}/rebuild-profile`,
        {},
        { headers: getAuthHeaders() }
      );
      setProfile(response.data);
      toast.success("Perfil musical global actualizado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No fue posible reconstruir el perfil global");
    } finally {
      setRebuildingId(null);
    }
  };

  const createAndLink = async () => {
    setCreating(true);
    try {
      const response = await axios.post(
        `${API_URL}/global-songs`,
        {
          canonical_title: analysis.filename,
          canonical_artist: null,
          aliases: [],
          duration_estimate: analysis.duration,
          primary_bpm: analysis.bpm,
          primary_key: analysis.key,
        },
        { headers: getAuthHeaders() }
      );
      await linkAnalysis(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No fue posible crear la canción global");
    } finally {
      setCreating(false);
    }
  };

  const rejectSuggestions = () => {
    setDismissed(true);
    trackProductEvent(
      "global_song_match_rejected",
      { analysis_id: analysis.id, candidate_count: candidates.length },
      getAuthHeaders
    );
  };

  if (!analysis || dismissed) return null;

  return (
    <Card className="border-emerald-500/20 bg-[#18181b] steel-studio-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2 text-base">
          <Globe2 className="w-5 h-5 text-emerald-300" />
          Global Song Intelligence
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 text-xs md:grid-cols-3 md:gap-3">
          <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-emerald-100">
            <ShieldCheck className="w-4 h-4 mb-1" />
            Tu audio permanece privado.
          </div>
          <div className="rounded-2xl bg-white/5 p-3 text-gray-300 ring-1 ring-white/10">
            Solo compartimos conocimiento musical abstracto.
          </div>
          <div className="rounded-2xl bg-white/5 p-3 text-gray-300 ring-1 ring-white/10">
            No compartimos stems ni audio.
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Buscando mapas musicales compatibles...
          </div>
        ) : candidates.length > 0 ? (
          <div className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-300">Posibles canciones globales para este análisis:</p>
              <Button variant="ghost" size="sm" onClick={rejectSuggestions} className="text-gray-400 hover:text-white gap-2">
                <X className="w-4 h-4" />
                No es ninguna
              </Button>
            </div>
            {candidates.map((candidate) => (
              <div
                key={candidate.global_song.id}
                className="flex flex-col justify-between gap-3 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10 md:flex-row md:items-center"
              >
                <div>
                  <p className="text-white font-medium">{candidate.global_song.canonical_title}</p>
                  <p className="text-xs text-gray-500">
                    {candidate.global_song.canonical_artist || "Artista no definido"} · confianza {formatConfidence(candidate.confidence)}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(candidate.reasons || []).slice(0, 4).map((reason) => (
                      <Badge key={reason} variant="outline" className="border-white/10 text-gray-300 bg-white/5">
                        {reason}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    variant="outline"
                    onClick={() => fetchProfile(candidate.global_song.id)}
                    disabled={profileLoadingId === candidate.global_song.id}
                    className="min-h-11 border-white/10 text-gray-200 gap-2"
                  >
                    {profileLoadingId === candidate.global_song.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Ver perfil
                  </Button>
                  <Button
                    onClick={() => linkAnalysis(candidate.global_song.id)}
                    disabled={linkingId === candidate.global_song.id || rebuildingId === candidate.global_song.id}
                    className="min-h-11 bg-emerald-600 hover:bg-emerald-700 gap-2"
                  >
                    {linkingId === candidate.global_song.id || rebuildingId === candidate.global_song.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    Usar canción existente
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <p className="text-sm text-white">No encontramos un mapa musical global confiable para este análisis.</p>
              <p className="text-xs text-gray-500">Puedes crear una entrada global con BPM, tonalidad y metadata abstracta.</p>
            </div>
            <Button onClick={createAndLink} disabled={creating} className="min-h-11 bg-emerald-600 hover:bg-emerald-700 gap-2">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Crear nueva canción
            </Button>
          </div>
        )}

        {profile && (
          <div className="space-y-3 rounded-2xl border border-primary/20 bg-primary/10 p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-white">Global Music Profile Available</p>
                <p className="text-xs text-gray-400">
                  Perfil agregado de {profile.source_count} fuente{profile.source_count === 1 ? "" : "s"} · confianza{" "}
                  {formatConfidence(profile.confidence)}
                </p>
              </div>
              <Badge className={profile.conflict_detected ? "bg-amber-500/20 text-amber-200" : "bg-emerald-500/20 text-emerald-200"}>
                {profile.conflict_detected ? "conflictos detectados" : "perfil consistente"}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
              <div className="rounded-xl bg-black/20 p-2 text-gray-200">BPM: {profile.bpm || "N/A"}</div>
              <div className="rounded-xl bg-black/20 p-2 text-gray-200">Key: {profile.key || "N/A"}</div>
              <div className="rounded-xl bg-black/20 p-2 text-gray-200">Secciones: {profile.sections?.length || 0}</div>
              <div className="rounded-xl bg-black/20 p-2 text-gray-200">Acordes: {profile.chords?.length || 0}</div>
            </div>
            <p className="text-xs text-gray-400">
              Puedes usar este perfil como referencia musical. No contiene audio, stems, recordings, presets ni URLs privadas.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GlobalSongMatchPanel;
